import { useContext, useMemo } from 'react';
import { CountryCode } from '@dayetopia/types';
import { useLocation, WindowLocation } from '@reach/router';
import { Context } from '@components/Context';
import { pageIsAdsPage } from './helpers';

export default function handleTextReplacement(text: string | null | undefined) {
  const {
    state: { country }
  } = useContext(Context);
  const location = useLocation();

  return useMemo(() => (text ? replaceText(text, country, location) : ''), [text, country]);
}

const landingURLs = [
  '1',
  '/products/organic-tampons/',
  '/products/organic-coated-tampons/',
  '/products/vaginal-health-proviotics/',
  '/festive-gifts/'
];

/**
 * Replaces text based on the country and page content.
 *
 * To ensure text replacement is applied:
 * - For US: No special action needed (full processing)
 * - For non-US: Only processes replacements for landing pages and ads pages
 *
 * @param text - The text to replace.
 * @param country - The country to replace the text for. Explicit return if country is not US.
 * @param location - The current URL. Vitals, landing pages and ads pages are handled differently.
 */
export function replaceText(text: string | null | undefined, country: CountryCode | null, location?: WindowLocation): string {
  const isPageForAds = pageIsAdsPage(location?.pathname ?? '');
  const isLandingPage = landingURLs.some((url) => typeof location !== 'undefined' && location.pathname.endsWith(url));
  const isVitalsPage = location?.pathname.includes('/vitals/');

  const replacements = [];

  if (country === CountryCode.US) {
    replacements.push(...replacementsUS);
    if (!isVitalsPage) {
      replacements.push(...nonVitalsReplacements);
    }
    if (isLandingPage || isPageForAds) {
      replacements.push(...CBDReplacements);
    }
  } else if (country === CountryCode.IE) {
    replacements.push(...replacementsIE);
  }

  return replaceTextWithReplacements(text, replacements);
}

const replaceTextWithReplacements = (text: string | null | undefined, replacements: string[][]) => {
  let newText = text ? String(text) : ''; // Ensure that `text` is always a string
  newText = newText.replace('https://#', '#');

  replacements.forEach(([before, after]) => {
    const regex = new RegExp(before.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
    newText = newText.replace(regex, (match) => {
      // Special case for CBD: don't match case
      if (before.toLowerCase().includes('cbd')) {
        return after;
      }

      const words = match.split(' ');
      const replacedWords = after.split(' ');
      return replacedWords
        .map((word, index) => {
          if (index >= words.length) return word;
          if (words[index] === 'I') return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          if (words[index] === words[index].toUpperCase()) return word.toUpperCase();
          if (words[index][0] === words[index][0].toUpperCase()) {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
          }
          return word;
        })
        .join(' ')
        .trim();
    });
  });

  return newText.trim();
};

const replacementsIE = [
  ['specialists', 'experts'],
  ['specialist', 'expert'],
  ['nurses', 'coaches'],
  ['nurse', 'coach']
];

const replacementsUS = [
  ['I can finally make it out of bed on my period now', 'Since subscribing to Daye, I’ve started looking forward to my period'],
  ['At-Home Vaginal Screen', 'At-Home Vaginal Testing'],
  ['Microbiome Screen', 'Microbiome Test'],
  ['Vaginal Microbiome Screening tampon', 'Microbiome Test'],
  ['Vaginal screening', 'Vaginal Microbiome Test'],
  ['Vaginal Microbiome Screening', 'Vaginal Microbiome Test'],
  ['Microbiome Screening', 'Vaginal Microbiome Test'],
  ['Vaginal Diagnostic Tampon Kit', 'Vaginal Microbiome Test'],
  ['Diagnostic Tampon Kit', 'Vaginal Microbiome Test'],
  ['microbiome screening', 'Vaginal Microbiome Testing'],
  ['Discover STI screening', 'Discover STI Testing'],
  ['Discover HPV screening', 'Discover HPV Testing'],
  ['tampon screen', 'tampon test'],
  ['Referral Programme', 'Referral Program'],
  ["Daye's Ambassador Programme", "Daye's Ambassador Program"],
  ['Request treatment for common vaginal infections like thrush and BV', 'Request treatment for common vaginal infections like yeast and BV'],
  ['It is the most common cause of yeast infections like thrush', 'It is the most common cause of yeast infections'],
  ['Comprising nothing but organic bamboo', 'Comprised of nothing but organic bamboo'],
  [
    'If you don’t feel the effects of our products, contact our customer care team and we’ll send you a refund!',
    'If you’re not 100% satisfied, contact our customer care team and we’ll send you a refund!'
  ],
  [
    '3 month subscriptions are billed and delivered up-front in instalments every 3 months',
    '3 month subscriptions are billed and delivered up-front in installments every 3 months'
  ],
  ['All prices include VAT.', 'All prices include applicable taxes.'],

  ['Helps prevent recurring vaginal infections like thrush and BV', 'Helps prevent recurring vaginal infections'],
  [
    'Having a high level of good bacteria <em>(Lactobacilli) </em>means your vaginal microbiome is thriving. Why should you care? Because it’s not just thrush and BV it wards off. A healthy vaginal microbiome has been known to lesson the risk of other vaginal health problems too.',
    'Because a healthy vaginal microbiome is essential to reproductive and gynaecological health.'
  ],
  ['infections like thrush and BV', 'yeast infections'],
  [
    'ProViotics have been clinically validated to survive in the acidic environment of both the gut and the vagina. You can take them orally to help relieve that pesky PMS bloat and improve your digestion. Making them a part of your daily self-care routine can lead to enhanced energy levels too — a win-win for the body and the mind.',
    'You can take them orally to help ease that pesky PMS bloat and enhance your digestion.'
  ],
  ['Enhanced energy levels', 'Heightened energy levels'],
  ['They’re clinically validated for efficacy in human studies.', ''],

  ['SANITISED FOR SAFETY', 'SANITISED'],
  ['remove the bacteria that cause Toxic Shock Syndrome (TSS).', 'remove harmful pathogens and bacteria.'],
  [
    'We offer tampons infused with a natural extract, clinically validated for safety and efficacy.',
    'We coat the tampons with a natural extract, which has been clinically tested for its safety.'
  ],
  ['Is Coated supposed to be brown', 'Are Coated Tampons supposed to be brown'],
  ['Why has my Coated changed', 'Why has my Coated Tampon changed'],
  ['Feel-good Coated extract', 'Feel-good extract'],
  ['Pharma grade Coated', 'Pharma grade coating'],
  ['analyse', 'analyze'],
  ['analysing', 'analyzing'],
  ['characterised', 'characterized'],
  ['maximise', 'maximize'],
  ['maximising', 'maximizing'],
  ['minimise', 'minimize'],
  ['minimising', 'minimizing'],
  ['customise', 'customize'],
  ['customising', 'customizing'],
  ['organisations', 'organizations'],
  ['anonymised', 'anonymized'],
  ['Sanitised', 'Sanitized'],
  ['personalised', 'personalized'],
  ['Specialised', 'Specialized'],
  ['specialised', 'specialized'],
  ['instalments', 'installments'],
  ['pre-term', 'preterm'],
  ['fibre', 'fiber'],
  ['favouring', 'favoring'],
  ['thrush', 'yeast infection'],
  ['colonise', 'colonize'],
  ['oestrogen', 'estrogen'],
  ['favourable', 'favorable'],
  ['unfavourable', 'unfavorable'],
  ['knickers', 'undies'],
  ['postcode', 'ZIP code'],
  [' post ', ' mail '],
  [' post.', ' mail.'],
  ['0ml', '0g'],
  ['24cm long', '10in long'],
  ['29cm long', '12in long'],
  ['35cm long', '14in long'],
  ['24cm', '9.6in'],
  ['29cm', '11.6in'],
  ['35cm', '14in'],
  ['39°C', '102°F'],
  ['labour', 'labor'],
  ['colour', 'color'],
  ['bespoke', 'custom'],
  ['Rigour', 'rigor'],
  ['Peri-menopausal', 'perimenopausal'],
  ['sanitisation', 'sanitization'],
  ['sanitise', 'sanitize'],
  ['sensitisation', 'sensitization'],
  ['localised', 'localized'],
  ['personalise', 'personalize'],
  ['recognise', 'recognize'],
  ['recognising', 'recognizing'],
  ['programme', 'program'],
  ['minimise', 'minimize'],
  ['Minimise', 'Minimize'],
  ['minimising', 'minimizing'],
  ['Minimising', 'Minimizing'],
  ['maximise', 'maximize'],
  ['Maximise', 'Maximize'],
  ['customise', 'customize'],
  ['Customise', 'Customize'],
  ['pre-term', 'preterm'],
  ['gynaecology', 'gynecology'],
  ['Gynaecology', 'Gynecology'],
  ['gynaecological', 'gynecological'],
  ['Gynaecological', 'Gynecological'],
  ['gynaecologist', 'gynecologist'],
  ['Gynaecologist', 'Gynecologist'],
  ['gynaecologists', 'gynecologists'],
  ['Gynaecologists', 'Gynecologists'],
  ['gynae', 'gynecological'],
  ['gynecological-curious', 'gynae-curious'],
  ['gonorrhoea', 'gonorrhea'],
  ['Gonorrhoea', 'Gonorrhea'],
  ['personalised', 'personalized'],
  ['Personalised', 'Personalized'],
  ['thrush', 'yeast infections'],
  ['Thrush', 'Yeast infections'],
  ['fertilisation', 'fertilization'],
  ['Fertilisation', 'Fertilization'],
  ['your bleed', 'your period'],
  ['the coil', 'intrauterine device (IUD)'],
  ['The coil', 'Intrauterine device (IUD)'],
  ['Hormonal coil', 'Intrauterine device'],
  ['hormonal coil', 'intrauterine device'],
  ['IUS', 'IUD'],
  ['Copper coil', 'Copper IUD (intrauterine device)'],
  ['copper coil', 'copper IUD (intrauterine device)'],
  ['practising', 'practicing'],
  ['odour', 'odor'],
  ['diarrhoea', 'diarrhea'],
  ['Diarrhoea', 'Diarrhea'],
  ['luteinising', 'luteinizing'],
  ['Luteinising', 'Luteinizing'],
  ['recognising', 'recognizing'],
  ['Recognising', 'Recognizing'],
  ['personalised', 'personalized'],
  ['Personalised', 'Personalized'],
  ['painkillers', 'pain-relief medication'],
  ['Painkillers', 'Pain-relief medication'],
  ['£300', '$300'], // PPC Savings
  ['prioritise', 'prioritize'],
  ['Prioritise', 'Prioritize'],
  ['diary', 'journal'],
  ['Diary', 'Journal'],
  ['specialises', 'specializes'],
  ['Specialises', 'Specializes'],
  ['specialised', 'specialized'],
  ['Specialised', 'Specialized'],
  ['recognise', 'recognize'],
  ['Recognise', 'Recognize'],
  ['maximise', 'maximize'],
  ['Maximise', 'Maximize'],
  ['39°C', '102°F'],
  ['1. Standard package (£14.99):', '1. Standard package ($14.99):'], // PPC FAQ
  ['2. Advanced package (£54.99):', '2. Advanced package ($69.99):'], // PPC FAQ
  [
    'The price also covers a pharmacist review and prescription fee, which you can use towards prescription tampons and Personalized contraception.',
    ''
  ], // PPC FAQ
  ['£24.99', '$34.99'],
  ['£14.99', '$14.99'],
  ['£44.99', '$69.99'],
  ['£54.99', '$69.99'],
  ['£199.99', '$299.99'],
  ['(£89)', ''], // STI Screening FAQ
  ['(£129)', ''], // STI Screening FAQ
  ['£2.5k', '$3k'], // Referrals
  ['£30,750', '$40,000'], // Referrals
  ['£5', '$5'], // Referrals
  ['recommend and prescribe', 'recommend'],
  ['to more innovative methods like TENS machines and prescription CBD tampons', 'to more innovative methods like TENS machines'],
  ['<em>Mycoplasma genitalium</em> and', ''],
  ['Pharmacist review fee', '']
];

const nonVitalsReplacements = [
  ['a sexual health nurse', 'a sexual & reproductive coach'],
  ['Expert Nurse Consultation', 'Sexual & Reproductive Coach Consultation'],
  ['Nurse Consultation', 'Sexual & Reproductive Coach Consultation'],
  ['nurses', 'coaches'],
  ['nurse', 'coach']
];

const CBDReplacements = [
  ['optional CBD coating', 'optional coating'],
  ['CBD & Nude Tampons', 'Nude & Coated Tampons'],
  ['FEEL-GOOD CBD', 'FEEL-GOOD TAMPONS'],
  ['CBD that hits different', 'Cannabinoids that hit different'],
  ['CBD balm', 'Hemp balm'],
  ['CBD tampons', 'extract infused tampons'],
  ['CBD', 'Coated'],
  [
    'We don’t recommend using Coated in any form if you are pregnant',
    'We don’t recommend using Coated tampons with CBD in any form if you are pregnant'
  ],
  [
    'While there’s no evidence showing that Coated could have an impact on fertility or pregnancy',
    'While there’s no evidence showing that CBD could have an impact on fertility or pregnancy'
  ],
  ['Your Coated tampon will have a colour as we never bleach our Coated', 'Your Coated Tampon will have a colour as we never bleach our CBD'],
  [
    'Coated tampons for the days when your period needs to get some chill. Revolutionary Coated-infused period care for feel good periods from Daye.',
    'CBD tampons for the days when your period needs to get some chill. Revolutionary CBD-infused period care for feel-good periods from Daye.'
  ],
  ['batch of Coated', 'batch of CBD'],
  ['amount of Coated', 'amount of CBD'],
  ['full-spectrum Coated', 'full-spectrum CBD'],
  ['cannabinoids', ''],
  ['cannabinoid', '']
];
